// Step 11 — Closing Confirmation (app.md §9). The queue of invoices at Pending
// Export, grouped by EXPORT FILE — the batch and the company together, which is
// the grain one CSV is written at. A set exported together stays together here
// and confirms together — all-or-nothing per file (v1.24): no per-invoice
// Confirm, no selection; each group header carries one green Confirm Import
// button behind the "Are you sure?" dialog.
//
// The header IS the file name, and it downloads that file (owner, 2026-08-28).
// Not the company name: an export batch is per company, so the same company
// reappears here every time it is exported and a company header would repeat
// without distinguishing anything. Below the header the group is the same shape
// it had at Export Control — gray count and total pills, an inline Expand all,
// an orange rule between groups — because these are the same invoices one step
// on.
//
// A group with no export batch is NOT a state the product can reach: Export
// Control stamps `export_id` in the same transaction that advances the invoice
// (`stampExportId`, `backend/src/repo/pg/invoices.ts`), and an invoice marked
// manually entered at Export Control never reaches this step. The fallback
// label below exists only so a broken row is visible rather than silently
// unconfirmable.
//
// Confirm is a decision per invoice (step 'Closing Confirmation Decision',
// 'approved') — the audit record. The API checks the role, advances each invoice
// to 'Imported Into Point Click Care' and closes the export batch out once
// its last invoice leaves Pending Export.
//
// HOLD: none here (owner, 2026-09-30 — D-243). The modal offers Release Hold
// only on an invoice held here before 2026-09-30, since a held invoice cannot
// move between steps (D-262). A held invoice is dimmed in its group and
// SKIPPED by Confirm Import: it stays here until its hold is released and a
// later Confirm Import takes it.
//
// TYPED INTO POINT CLICK CARE, per invoice, in the modal (owner,
// 2026-10-07: "add the Manually Entered Into Point Click Care button that we
// recently added to Export Control now at Closing Confirmation ... it would
// also need to replace the csv for that group of invoices"). The same green
// button and pop-up as Export Control's; the server ends the invoice and
// rebuilds its file without it as a new file, keeping the old one, or
// withdraws the file when nothing is left in it. The header then names the new
// file, with its version.
//
// PORT NOTE: the batch is one request now, each invoice its own transaction, and
// the response says per invoice whether it applied and why not. The old loop wrote
// the decisions one at a time and then waited on a background process, so a
// partial failure left half a batch "submitting" with a Retry that had to work out
// which rows to re-attempt.
import { DECISION_SOURCE } from '@ap/shared'
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Link,
  Subtitle2,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import {
  ChevronDoubleDownRegular,
  ChevronDoubleUpRegular,
} from '@fluentui/react-icons'
import type { ExportDetail, ExportFile, Invoice } from '../lib/types'
import type { ReasonCategory } from '../lib/constants'
import {
  DecisionOutcome,
  DecisionStep,
  InvoiceStatus,
  MANUAL_ENTRY_CATEGORIES,
  SCREEN_SUBHEADER,
  TOASTER_ID,
} from '../lib/constants'
import type { RefreshSignal } from '../lib/refresh'
import { useQueue } from '../lib/hooks'
import { createDecision, createDecisionBatch, getExport, getExportFileUrl, loadInvoicesByExportKey } from '../lib/data'
import { downloadUrl } from '../lib/download'
import { errorMessage } from '../api/errors'
import { canDecide, filterDecidable, type Session } from '../lib/identity'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { NeutralPill } from '../components/Badges'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { formatCurrency, formatDateTime } from '../lib/format'
import { useDecisionButtonStyles, useQueueToolbarButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner, WarningBanner } from '../components/States'
import { actionA11y, heldReason, useActiveHolds } from '../lib/holds'
import { codingGateReason } from '../lib/required'
import { ScreenIntro } from '../components/ScreenIntro'

const useStyles = makeStyles({
  groups: { display: 'flex', flexDirection: 'column', gap: '28px' },
  // A gap below the header so the green action button never sits flush against
  // the first invoice card under it (owner, 2026-08-31).
  groupHead: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  spacer: { flexGrow: 1 },
  // The group's title IS the download link for its file, so it is sized like
  // the `Subtitle2` heading it replaced rather than like body text.
  fileLink: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
  },
  // The orange batch divider — same rule as Export Control's header/groups.
  groupRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
  // The same rule under the page header, matching Export Control's.
  headerRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px', marginBottom: '16px' },
})

/** One export file's worth of invoices — the unit this screen displays and
    confirms. `file` is absent only when the batch lookup failed or the batch
    wrote no file for this company. */
interface FileGroup {
  key: string
  exportId: string
  companyId: string
  companyName: string
  exportedAt: string
  file?: ExportFile
  /** The rows the page filter left visible. */
  invoices: Invoice[]
  /** Every row in the group, filter or no filter — what Confirm Import acts on. */
  allIds: string[]
  /** Sum of the visible rows' totals — the header's Total pill. */
  total: number
}

interface Props {
  session: Session
  /** The shell's refresh signal (D-160): the Refresh and Pipeline buttons and the 30-second timer. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called after confirms land, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ClosingConfirmationScreen({ session, refresh, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const toolbarBtn = useQueueToolbarButtonStyles()
  const user = session.user
  // Declared ahead of `useQueue`: the queue is PAUSED while any of these is
  // open or in flight, so a background refresh never moves the gallery under a
  // modal or dialog; it catches up silently on close (D-160).
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [bulkPending, setBulkPending] = useState<FileGroup | null>(null)
  /** The invoice whose Typed Into Point Click Care pop-up is open. */
  const [manualPending, setManualPending] = useState<Invoice | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingExport], refresh, {
    paused: openInvoice !== null || bulkPending !== null || manualPending !== null || submitting,
  })
  const pageFiltered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  // Owner ruling, 2026-08-14: a caller without the Closing Confirmation role
  // sees an empty queue here, not a view-only one — Closing Confirmation is
  // role-gated, so this reduces to "every row, or none", but it keeps this
  // screen consistent with the same rule every other decision screen follows.
  const filtered = useMemo(
    () => filterDecidable(session, DecisionStep.ClosingConfirmation, pageFiltered),
    [pageFiltered, session],
  )
  // Expand all / Collapse all per group, lifted out of `InvoiceQueue` so the
  // button sits inline beside Confirm Import — the same move Export Control's
  // group header makes (`ApprovalScreen`).
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])

  // HOLDS. None is placed or released here (D-243; code review C-13), and none
  // can arrive held (D-262). A held row would still be dimmed and skipped, by
  // the rule every queue follows.
  const { holdFor, isHeld, heldIdsIn } = useActiveHolds(invoices)
  // A row that says held is held before its hold record arrives (D-263).
  const heldIds = useMemo(() => heldIdsIn(filtered), [filtered, heldIdsIn])
  // The open invoice as the queue now has it, for the hold answer: the click
  // copy keeps the hold state of the moment it was read (D2-01, D1-05).
  const openInvoiceLive = openInvoice ? (byId.get(openInvoice.id) ?? openInvoice) : null

  // Authorization mirror (canDecide) — the API checks the Closing Confirmation
  // role for real; the button disables with the reason here.
  const authorized = invoices.length === 0 || canDecide(session, DecisionStep.ClosingConfirmation, invoices[0])
  const notAuthorizedReason =
    "You don't hold the Closing Confirmation role. You can view these invoices, but your confirmation would be refused."

  // The export batch behind each key, WITH ITS FILES — the header names the
  // file and links to it, so the file list is the point of this lookup, not
  // just the batch's timestamp. A missing batch falls back to a stub label.
  const [exportsByKey, setExportsByKey] = useState<Record<string, ExportDetail>>({})
  // Key on a stable, sorted join of the distinct export keys so a queue reload
  // (same rows, new array) doesn't refire this lookup.
  const exportKeyJoin = useMemo(
    () => [...new Set(invoices.map((i) => i.export_id).filter((k): k is string => !!k))].sort().join(','),
    [invoices],
  )
  useEffect(() => {
    const keys = exportKeyJoin ? exportKeyJoin.split(',') : []
    if (keys.length === 0) {
      setExportsByKey({})
      return
    }
    // allSettled, not all: one failed lookup must not drop every batch label.
    Promise.allSettled(keys.map((key) => getExport(key))).then((results) => {
      const next: Record<string, ExportDetail> = {}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) next[keys[i]] = r.value
      })
      setExportsByKey(next)
    })
  }, [exportKeyJoin])

  // ONE GROUP PER EXPORT FILE — the batch and the company together, oldest
  // export first. A batch is written per company, so this is normally one group
  // per batch; a company that was exported twice gets two groups, which is
  // exactly why the header cannot be the company name. Display grouping follows
  // the filtered view; the confirm always covers the FULL group
  // (all-or-nothing), so its ids come from the unfiltered queue.
  const groups = useMemo<FileGroup[]>(() => {
    const keyOf = (inv: Invoice) => `${inv.export_id ?? ''}::${inv.company_id ?? ''}`
    const byKey = new Map<string, Invoice[]>()
    for (const inv of filtered) {
      const rows = byKey.get(keyOf(inv))
      if (rows) rows.push(inv)
      else byKey.set(keyOf(inv), [inv])
    }
    return [...byKey.entries()]
      .map(([key, rows]) => {
        const exportId = rows[0].export_id ?? ''
        const companyId = rows[0].company_id ?? ''
        const detail = exportsByKey[exportId]
        // The company's own file, or — for a batch that wrote just one — that
        // one, whatever company it names. A split-company invoice puts part of
        // itself in another company's file; the header names this group's.
        const files = detail?.files ?? []
        const file = files.find((f) => (f.company_id ?? '') === companyId) ?? (files.length === 1 ? files[0] : undefined)
        return {
          key,
          exportId,
          companyId,
          companyName: rows[0].company_name ?? '',
          exportedAt: detail?.export.exported_at ?? '',
          file,
          invoices: rows,
          allIds: invoices.filter((i) => keyOf(i) === key).map((i) => i.id),
          total: rows.reduce((sum, i) => sum + (i.total ?? 0), 0),
        }
      })
      .sort(
        (a, b) =>
          a.exportedAt.localeCompare(b.exportedAt) || a.companyName.localeCompare(b.companyName),
      )
  }, [filtered, invoices, exportsByKey])

  // Rows the page filter hides inside displayed groups. Worth a callout,
  // because Confirm Import covers the FULL group either way.
  const hiddenInBatches = groups.reduce((sum, g) => sum + (g.allIds.length - g.invoices.length), 0)

  /** The header when there is no file to name — see this file's header comment:
      not a state Export Control can produce, so it reads as the fault it is. */
  function fallbackLabel(g: FileGroup): string {
    if (!g.exportId) return `${g.companyName || 'These invoices'} — export file not recorded`
    const detail = exportsByKey[g.exportId]
    if (!detail) return `Export batch ${g.exportId.slice(0, 8)}`
    const who = detail.export.exported_by_name || detail.export.exported_by_email
    return `Exported ${formatDateTime(detail.export.exported_at)}${who ? ` by ${who}` : ''}`
  }

  /** Fetch a short-lived signed URL for the copy of record and save it. The
      stored object is the record; this is the controller's copy to import. */
  async function download(g: FileGroup, file: ExportFile) {
    try {
      const signed = await getExportFileUrl(g.exportId, file.file_id)
      downloadUrl(signed.url, signed.file_name ?? file.file_name)
    } catch (e) {
      setActionError(`Couldn't download ${file.file_name}: ${errorMessage(e)}`)
    }
  }

  /**
   * Typed Into Point Click Care, for ONE invoice (owner,
   * 2026-10-07). The decision replaces the invoice's file on the server; the
   * batch is read again afterwards so the header names the new file, and the
   * toast says whether a file is left.
   */
  async function markManuallyEntered(inv: Invoice, reason?: string, category?: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: inv.id,
        step: DecisionStep.ClosingConfirmation,
        decision: DecisionOutcome.RequiresManualEntry,
        reason: reason || undefined,
        reason_category: (category as ReasonCategory) || undefined,
        row_version: inv.row_version,
        // Its one button is in the invoice modal (D-331).
        source: DECISION_SOURCE.modal,
      })
      setManualPending(null)
      setOpenInvoice(null)
      let withdrawn = false
      if (inv.export_id) {
        const exportId = inv.export_id
        try {
          const detail = await getExport(exportId)
          setExportsByKey((prev) => ({ ...prev, [exportId]: detail }))
          withdrawn = detail.files.some((f) => (f.company_id ?? '') === (inv.company_id ?? '') && f.withdrawn)
        } catch {
          // The decision stands; the header catches up on the next refresh.
        }
      }
      void reload({ silent: true })
      onQueuesChanged?.()
      dispatchToast(
        <Toast>
          <ToastTitle>The invoice was marked as manually entered into Point Click Care.</ToastTitle>
          <ToastBody>
            {withdrawn
              ? 'No invoice is left in its export file, so the file was withdrawn. The earlier file is kept.'
              : 'Its export file was replaced by a new one without it. The earlier file is kept.'}
          </ToastBody>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  // Re-read the batch from the server before confirming it. Confirm Import means
  // the whole group, and the queue behind an open dialog may have moved on — this
  // is the one place the full membership matters, so it is read rather than
  // inferred from what happens to be loaded. The re-read is per BATCH (the only
  // filter the API offers), then narrowed to this group's company, which is how
  // the group was drawn.
  async function confirm(group: FileGroup) {
    setSubmitting(true)
    setActionError(null)
    try {
      // HELD INVOICES ARE SKIPPED (owner, 2026-09-25): the server would refuse
      // each one anyway, and a hold means "not yet", not "fail the file". The
      // re-read carries each row's own `has_active_hold`.
      let batchIds = group.allIds.filter((id) => !isHeld(byId.get(id)))
      if (group.exportId) {
        const rows = (await loadInvoicesByExportKey(group.exportId, InvoiceStatus.PendingExport)).filter(
          (i) => (i.company_id ?? '') === group.companyId,
        )
        if (rows.length > 0) batchIds = rows.filter((i) => !i.has_active_hold).map((i) => i.id)
      }
      if (batchIds.length === 0) {
        setBulkPending(null)
        dispatchToast(
          <Toast>
            <ToastTitle>Nothing was confirmed, because every invoice in this file is on hold.</ToastTitle>
          </Toast>,
          { intent: 'info' },
        )
        return
      }
      const result = await createDecisionBatch({
        step: DecisionStep.ClosingConfirmation,
        decision: DecisionOutcome.Approved,
        invoice_ids: batchIds,
        // The file group's Confirm, on the queue (D-331).
        source: DECISION_SOURCE.bulk_queue,
      })
      setBulkPending(null)
      setOpenInvoice(null)
      // Silent (owner, 2026-09-28): a loud reload swaps the list for a
      // placeholder, the page shrinks, and the reviewer is thrown to the top.
      void reload({ silent: true })
      onQueuesChanged?.()
      const refusals = result.results.filter((r) => !r.applied)
      if (refusals.length > 0) {
        const named = refusals
          .map((r) => {
            const inv = byId.get(r.invoice_id)
            const label = inv?.external_invoice_id || inv?.vendor_name || r.invoice_id
            return `${label}: ${r.error?.message ?? 'refused'}`
          })
          .join('; ')
        setActionError(
          `${result.applied_count} of ${result.results.length} confirmed. Not confirmed — ${named}`,
        )
      }
      const n = result.applied_count
      dispatchToast(
        <Toast>
          <ToastTitle>
            {n === 1
              ? 'One invoice was bulk imported into Point Click Care, and it is on its way to payment.'
              : `${n} invoices were bulk imported into Point Click Care, and they are on their way to payment.`}
          </ToastTitle>
          {refusals.length > 0 && <ToastBody>{refusals.length} stayed in the queue.</ToastBody>}
        </Toast>,
        { intent: refusals.length > 0 ? 'warning' : 'success' },
      )
    } catch (e) {
      setActionError(`Confirm failed: ${errorMessage(e)}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <ScreenIntro>{SCREEN_SUBHEADER.ClosingConfirmation}</ScreenIntro>
      <div className={s.headerRule} aria-hidden />

      <ErrorBanner message={error || actionError} />
      {/* Clears itself when the filters do (no dismiss). */}
      {hiddenInBatches > 0 && (
        <WarningBanner
          message={`The active filters hide ${hiddenInBatches} invoice${
            hiddenInBatches === 1 ? '' : 's'
          } inside the groups below. Confirm Import always confirms a file's full contents — hidden invoices included.`}
        />
      )}

      {loading ? (
        <Loading label="Loading invoices pending export…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue
          text={
            pageFiltered.length > 0
              ? "You don't hold the Closing Confirmation role."
              : invoices.length > 0
                ? 'No invoices match the current filters.'
                : 'No invoices awaiting closing confirmation.'
          }
        />
      ) : (
        <div className={s.groups}>
          {groups.map((g, idx) => {
            const file = g.file
            const confirmImportWhy = authorized
              ? 'Confirm the Point Click Care import completed for every invoice in this file'
              : notAuthorizedReason
            return (
              <Fragment key={g.key}>
                {idx > 0 && <div className={s.groupRule} aria-hidden />}
                <section>
                  <div className={s.groupHead}>
                    {file ? (
                      <Link
                        as="button"
                        className={s.fileLink}
                        title={`Download ${file.file_name}`}
                        onClick={() => void download(g, file)}
                      >
                        {file.file_name}
                      </Link>
                    ) : (
                      <Subtitle2>{fallbackLabel(g)}</Subtitle2>
                    )}
                    {/* The count and the total, as at Export Control — the
                        per-group summary box they used to sit in is gone
                        (owner, 2026-08-28). */}
                    {/* A REPLACED FILE SAYS SO (owner, 2026-10-07): the name
                        already ends in its version, and the pill says why. */}
                    {file && file.version > 1 && (
                      <NeutralPill title="This file was rebuilt without the invoices marked as manually entered into Point Click Care. The earlier files are kept.">
                        Replaced
                      </NeutralPill>
                    )}
                    <NeutralPill>
                      {g.allIds.length} invoice{g.allIds.length === 1 ? '' : 's'}
                    </NeutralPill>
                    <NeutralPill title="Sum of these invoices' totals">
                      {formatCurrency(g.total)} Total
                    </NeutralPill>
                    <div className={s.spacer} />
                    <Button
                      appearance="subtle"
                      className={toolbarBtn.subtle}
                      icon={expandedGroups[g.key] ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />}
                      onClick={() => setExpandedGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                    >
                      {expandedGroups[g.key] ? 'Collapse All' : 'Expand All'}
                    </Button>
                    <Button
                      // Medium, matching Export Control's Export and the
                      // approval screens' "Approve Selected" (owner,
                      // 2026-08-28).
                      className={btn.approve}
                      disabled={submitting || !authorized}
                      title={confirmImportWhy}
                      // NAMED BY ITS LABEL, PLUS WHICH FILE (QA 2026-09-30,
                      // C2-03). Every group's button was read as the tooltip,
                      // seven identical names without "Confirm Import" in
                      // them. D-157: a reason it is disabled (authority) stays
                      // in the name after the label; the enabled button's
                      // tooltip is its description.
                      {...actionA11y(`Confirm Import, ${file ? file.file_name : fallbackLabel(g)}`, {
                        reason: authorized ? undefined : notAuthorizedReason,
                      })}
                      aria-description={authorized ? confirmImportWhy : undefined}
                      onClick={() => setBulkPending(g)}
                    >
                      Confirm Import
                    </Button>
                  </div>
                  <InvoiceQueue
                    invoices={g.invoices}
                    screen={DecisionStep.ClosingConfirmation}
                    columns="amounts"
                    onOpen={setOpenInvoice}
                    dimmedIds={heldIds}
                    heldIds={heldIds}
                    codingDisplay="halves"
                    expandAll={!!expandedGroups[g.key]}
                  />
                </section>
              </Fragment>
            )
          })}
        </div>
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        user={user}
        screen={DecisionStep.ClosingConfirmation}
        onClose={() => setOpenInvoice(null)}
        // [TYPED INTO POINT CLICK CARE] alone. No Hold here (owner, 2026-09-30
        // — D-243), and no Release Hold either since code review C-13
        // (2026-10-17): a held invoice cannot move between steps (D-262), so
        // none can arrive here held. The green button is the forward action, as
        // at Export Control (owner, 2026-10-07); Confirm Import stays a
        // per-file button outside the modal. Disabled while held, without the
        // role, or while a line is missing its coding — the server refuses the
        // same (`codingGated`).
        actions={
          openInvoiceLive &&
          (() => {
            const decidable = canDecide(session, DecisionStep.ClosingConfirmation, openInvoiceLive)
            const hold = holdFor(openInvoiceLive)
            const heldTitle = hold ? heldReason(hold) : undefined
            const codingReason = codingGateReason(
              {
                missing: openInvoiceLive.uncoded_line_item_count ?? 0,
                capital: openInvoiceLive.capital_equipment_under_floor_count ?? 0,
                marketing: openInvoiceLive.missing_marketing_category_count ?? 0,
              },
              (glOnly) =>
                glOnly ? 'Finish the GL coding before confirming entry.' : 'Finish the coding before confirming entry.',
            )
            const otherReason = codingReason ?? (decidable ? undefined : notAuthorizedReason)
            return (
              <>
                {/* No icon on a labelled modal button (owner, 2026-09-30, D-245). */}
                <Button
                  className={btn.approve}
                  disabled={submitting || !decidable || !!hold || codingReason !== undefined}
                  title={heldTitle ?? otherReason ?? 'Confirm this invoice was entered into Point Click Care'}
                  {...actionA11y('Typed Into Point Click Care', {
                    held: heldTitle,
                    reason: otherReason,
                  })}
                  onClick={() => setManualPending(openInvoiceLive)}
                >
                  Typed Into Point Click Care
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!bulkPending}
        title="Are you sure?"
        body={
          (bulkPending?.allIds.length ?? 0) - (bulkPending?.allIds.filter((id) => heldIds.has(id)).length ?? 0) === 1
            ? 'You are confirming that the Point Click Care import for this invoice is complete, meaning that it is on its way to payment. This will remove this invoice from the pipeline here. You cannot undo this action.'
            : 'You are confirming that the Point Click Care import for these invoices is complete, meaning that they are on their way to payment. This will remove these invoices from the pipeline here. You cannot undo this action.'
        }
        note={(() => {
          const held = bulkPending?.allIds.filter((id) => heldIds.has(id)).length ?? 0
          if (held === 0) return undefined
          return held === 1
            ? 'One invoice in this file is on hold. It is skipped and stays here until the hold is released.'
            : `${held} invoices in this file are on hold. They are skipped and stay here until their holds are released.`
        })()}
        requireReason={false}
        confirmLabel="Confirm Import"
        submitting={submitting}
        onCancel={() => setBulkPending(null)}
        onConfirm={() => {
          if (!bulkPending) return
          void confirm(bulkPending)
        }}
      />

      {/* EXPORT CONTROL'S POP-UP, word for word (owner, 2026-10-07: "it would
          work in the same way"): the PCC Import Error / Other categories, the
          Reason only under Other (D-316), the same example and sentence. The
          note is the one thing this step adds — what happens to the file. */}
      <DecisionDialog
        open={!!manualPending}
        title="Are you sure?"
        body="You are confirming that this invoice has been manually entered into Point Click Care, meaning that it is on its way to payment. This will remove this invoice from the pipeline here. You cannot undo this action."
        note="Its export file will be replaced by a new one without this invoice, or withdrawn if no invoice is left in it. The earlier file is kept."
        requireReason
        reasonLabel="Comment"
        reasonPlaceholder="The 1099 Amount on one of this invoice's line items exceeded the invoice's Total, blocking import into PCC."
        categories={MANUAL_ENTRY_CATEGORIES}
        confirmLabel="Typed Into Point Click Care"
        confirmClassName={btn.approve}
        submitting={submitting}
        onCancel={() => setManualPending(null)}
        onConfirm={(reason, category) => {
          if (!manualPending) return
          void markManuallyEntered(manualPending, reason, category)
        }}
      />
    </div>
  )
}
